import {
  PAGINATION_ELLIPSIS_END,
  PAGINATION_ELLIPSIS_START,
  type TPaginationRangeItem,
} from "@/ui/components/navigation/Pagination/Pagination.config";

const createRange = (start: number, end: number): number[] =>
  Array.from({ length: Math.max(end - start + 1, 0) }, (_, index) => start + index);

// The 5 fixed slots a full range always keeps: the first page, the last page,
// the current page, and one ellipsis on each side. Everything below this many
// total pages fits without truncating, so we just render every page.
const RESERVED_SLOT_COUNT = 5;

// When only one side needs an ellipsis, the other (un-truncated) side shows a
// solid run of pages: the edge page (first or last), the current page, and a
// sibling on each side.
const EDGE_BLOCK_COUNT = 3;

/**
 * Builds the ordered list of page numbers (and ellipsis markers) to render,
 * always keeping the first and last page visible plus `siblingCount` pages on
 * either side of the current page. Ellipsis markers fill the gaps.
 */
export const getPaginationRange = ({
  page,
  totalPages,
  siblingCount = 1,
}: {
  page: number;
  totalPages: number;
  siblingCount?: number;
}): TPaginationRangeItem[] => {
  if (totalPages <= 0) {
    return [];
  }

  const currentPage = Math.min(Math.max(page, 1), totalPages);

  const totalVisiblePages = siblingCount * 2 + RESERVED_SLOT_COUNT;

  if (totalVisiblePages >= totalPages) {
    return createRange(1, totalPages);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);

  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  const edgeItemCount = siblingCount * 2 + EDGE_BLOCK_COUNT;

  if (!showLeftEllipsis && showRightEllipsis) {
    return [ ...createRange(1, edgeItemCount), PAGINATION_ELLIPSIS_END, totalPages ];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    return [ 1, PAGINATION_ELLIPSIS_START, ...createRange(totalPages - edgeItemCount + 1, totalPages) ];
  }

  return [
    1,
    PAGINATION_ELLIPSIS_START,
    ...createRange(leftSibling, rightSibling),
    PAGINATION_ELLIPSIS_END,
    totalPages,
  ];
};
